import { auth } from '../core/auth-store.js';
import { on } from '../core/event-bus.js';

export class AppNav extends HTMLElement {
  private cleanup: Array<() => void> = [];

  connectedCallback(): void {
    this.render();
    this.cleanup.push(
      auth.subscribe(() => this.render()),
      on('hashchange-internal', () => this.render()),
    );
    window.addEventListener('hashchange', this.onHashChange);
  }

  disconnectedCallback(): void {
    this.cleanup.forEach(fn => fn());
    window.removeEventListener('hashchange', this.onHashChange);
  }

  private onHashChange = () => this.render();

  private render(): void {
    const current = location.hash.slice(1) || '/';
    const loggedIn = auth.isLoggedIn;
    const isAdmin = auth.isAdmin;

    const publicLinks = [
      { hash: '/availability', label: 'Dost\u0119pno\u015b\u0107', icon: '\u{1F4CB}' },
      { hash: '/schedule', label: 'Grafik', icon: '\u{1F4C5}' },
    ];

    const userLinks = [
      ...publicLinks,
      { hash: '/profile', label: 'Moje dane', icon: '\u{1F464}' },
    ];

    const adminLinks = [
      { hash: '/admin/dashboard', label: 'Panel', icon: '\u{1F4CA}' },
      { hash: '/admin/people', label: 'Osoby', icon: '\u{1F465}' },
      { hash: '/admin/roles', label: 'Role', icon: '\u{1F3AD}' },
      { hash: '/admin/quarters', label: 'Kwarta\u0142y', icon: '\u{1F4C6}' },
      { hash: '/admin/schedule', label: 'Grafik', icon: '\u{1F4DD}' },
      { hash: '/admin/matrix', label: 'Macierz', icon: '\u{1F532}' },
      { hash: '/admin/settings', label: 'Ustawienia', icon: '\u2699\uFE0F' },
    ];

    let links: typeof userLinks;
    if (!loggedIn) links = publicLinks;
    else if (isAdmin && !auth.isImpersonating) links = adminLinks;
    else links = userLinks;

    // Top bar: brand+login or avatar+logout
    let topBarHtml: string;
    if (!loggedIn) {
      topBarHtml = `
        <header class="top-bar">
          <a href="#/schedule" class="top-bar-brand">
            <img src="/favicon.svg" alt="" class="top-bar-logo">
            <span class="top-bar-name">Szko\u0142a Sobotnia</span>
          </a>
          <a href="#/login" class="top-bar-login">Zaloguj</a>
        </header>`;
    } else {
      const name = auth.displayName ?? '';
      const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
      topBarHtml = `
        <header class="top-bar">
          <div class="top-bar-user">
            <div class="top-bar-avatar">${initials}</div>
            <span class="top-bar-name">${name}</span>
            ${isAdmin ? '<span class="top-bar-badge">Admin</span>' : ''}
          </div>
          <button class="top-bar-logout" id="logout-btn" title="Wyloguj">Wyloguj</button>
        </header>`;
    }

    this.innerHTML = `
      ${topBarHtml}
      <nav class="app-nav">
        ${links.map(l => `
          <a href="#${l.hash}" class="nav-link ${current.startsWith(l.hash) ? 'active' : ''}">
            <span class="nav-icon">${l.icon}</span>
            <span class="nav-label">${l.label}</span>
          </a>
        `).join('')}
      </nav>
    ` + this.sharedStyles();

    this.querySelector('#logout-btn')?.addEventListener('click', () => {
      auth.logout();
      location.hash = '#/schedule';
    });
  }

  private sharedStyles(): string {
    return `
      <style>
        .top-bar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          height: var(--header-height);
          background: var(--color-primary);
          color: var(--color-on-primary);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 var(--space-md);
          box-shadow: var(--shadow-md);
        }
        .top-bar-brand {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          text-decoration: none;
          color: var(--color-on-primary);
        }
        .top-bar-logo {
          width: 28px;
          height: 28px;
          flex-shrink: 0;
        }
        .top-bar-user {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          min-width: 0;
        }
        .top-bar-avatar {
          width: 36px;
          height: 36px;
          border-radius: var(--radius-full);
          background: var(--color-primary-dark);
          color: var(--color-on-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: .85rem;
          font-weight: 700;
          flex-shrink: 0;
        }
        .top-bar-name {
          font-size: 1rem;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .top-bar-badge {
          font-size: .65rem;
          font-weight: 700;
          text-transform: uppercase;
          background: rgba(255,255,255,.2);
          padding: 2px 8px;
          border-radius: var(--radius-full);
          flex-shrink: 0;
        }
        .top-bar-login,
        .top-bar-logout {
          background: none;
          border: 1px solid rgba(255,255,255,.3);
          color: var(--color-on-primary);
          padding: 6px 14px;
          border-radius: var(--radius-sm);
          font-size: .85rem;
          cursor: pointer;
          flex-shrink: 0;
          transition: background .15s;
          text-decoration: none;
        }
        .top-bar-login:hover,
        .top-bar-logout:hover { background: rgba(255,255,255,.15); }

        .app-nav {
          display: flex;
          background: var(--color-surface);
          border-top: 1px solid var(--color-surface-container);
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 100;
          padding: 4px 0;
        }
        .nav-link {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          padding: 8px 4px;
          text-decoration: none;
          color: var(--color-on-surface-muted);
          font-size: .7rem;
          border: none;
          background: none;
          cursor: pointer;
          transition: color .15s;
        }
        .nav-link.active { color: var(--color-primary); font-weight: 600; }
        .nav-link:hover { color: var(--color-primary); }
        .nav-icon { font-size: 1.25rem; }

        @media (min-width: 800px) {
          .top-bar { left: var(--nav-width); }
          .app-nav {
            flex-direction: column;
            position: fixed;
            left: 0;
            top: 0;
            bottom: 0;
            right: auto;
            width: var(--nav-width);
            border-top: none;
            border-right: 1px solid var(--color-surface-container);
            padding: 16px 0;
          }
          .nav-link { flex: 0; padding: 12px 8px; }
        }
      </style>
    `;
  }
}

customElements.define('app-nav', AppNav);
